"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqItems: FAQItem[] = [
    {
      id: "faq_1",
      question: "How is this different from coaching?",
      answer:
        "Coaching provides guidance and advice. This diagnostic surfaces patterns without interpretation. You receive your responses for review by Horváth Anett, not automated feedback or prescriptive solutions.",
    },
    {
      id: "faq_2",
      question: "What happens to my responses?",
      answer:
        "Your responses are sent directly to Horváth Anett for personal review. They are not stored in any automated system, shared with third parties, or used for any purpose other than your diagnostic analysis. Full confidentiality is maintained.",
    },
    {
      id: "faq_3",
      question: "How long does the diagnostic take?",
      answer:
        "The diagnostic consists of 15 questions, one per screen. Most leaders complete it in 10-15 minutes. There is no time pressure—take as long as you need to consider each question.",
    },
    {
      id: "faq_4",
      question: "Will I receive my results immediately?",
      answer:
        "No. This is not an automated assessment. After submission, you will receive a confirmation. Horváth Anett will personally review your responses and contact you to discuss the patterns observed. This preserves the diagnostic integrity and prevents misinterpretation.",
    },
    {
      id: "faq_5",
      question: "Is this confidential?",
      answer:
        "Yes. All responses are treated with complete confidentiality. Your diagnostic data is only accessible to Horváth Anett and is never shared, published, or used for any other purpose without explicit consent.",
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 lg:py-32 border-t border-border">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-6 mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-border bg-secondary/50">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Questions & Answers
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div
                key={item.id}
                className={`glass-card overflow-hidden transition-all duration-300 ${
                  openIndex === index ? "border-accent/30" : ""
                }`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left focus-ring"
                >
                  <h3 className="text-lg font-bold text-foreground pr-4">
                    {item.question}
                  </h3>
                  <div
                    className={`size-8 rounded-full border border-border flex items-center justify-center text-muted flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? "rotate-180 border-accent text-accent" : ""
                    }`}
                  >
                    <Icon name="ChevronDownIcon" size={16} />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? "max-h-96" : "max-h-0"
                  }`}
                >
                  <div className="px-6 pb-6 text-base text-muted-foreground leading-relaxed">
                    {item.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}